'use client';

import { CheckCircle2, RefreshCw, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useOffline } from '@/contexts/OfflineProvider';
import { useMountedPathname } from '@/hooks/useMountedPathname';

type NetworkState = 'online' | 'offline' | 'degraded' | 'restored';

const PILL_BASE =
  'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs font-medium shadow-sm backdrop-blur-sm';

const PILL_TONES: Record<NetworkState, string> = {
  online: 'border-[var(--color-info)]/20 bg-[var(--color-info-soft)] text-[var(--color-info)]',
  offline: 'border-[var(--color-danger)]/20 bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  degraded: 'border-[var(--color-warning)]/20 bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  restored: 'border-[var(--color-success)]/20 bg-[var(--color-success-soft)] text-[var(--color-success)]',
};

export function GlobalOfflineIndicator() {
  const { isOnline, isDegraded, wasOffline, pendingCount } = useOffline();
  const pathname = useMountedPathname();
  const [networkState, setNetworkState] = useState<NetworkState>('online');

  useEffect(() => {
    if (wasOffline && isOnline) {
      setNetworkState('restored');
      const timer = window.setTimeout(() => setNetworkState('online'), 3000);
      return () => window.clearTimeout(timer);
    } else if (!isOnline) {
      setNetworkState('offline');
    } else if (isDegraded) {
      setNetworkState('degraded');
    } else {
      setNetworkState('online');
    }
  }, [isOnline, isDegraded, wasOffline]);

  // POS has its own workspace-aware indicator, so the global pill would just
  // stack on top of it. Let the POS shell own the notice there.
  if (pathname?.startsWith('/pos/')) return null;

  if (networkState === 'online' && pendingCount === 0) return null;

  const offline = networkState === 'offline' || networkState === 'degraded';

  const icon =
    networkState === 'restored' ? (
      <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" />
    ) : networkState === 'online' ? (
      <RefreshCw className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
    ) : (
      <WifiOff className="h-3 w-3 shrink-0" aria-hidden="true" />
    );

  const message = (() => {
    if (networkState === 'restored') {
      return pendingCount > 0
        ? `Back online — syncing ${pendingCount} pending action${pendingCount !== 1 ? 's' : ''}…`
        : 'Back online';
    }
    if (networkState === 'online') {
      return `Syncing ${pendingCount} pending action${pendingCount !== 1 ? 's' : ''}…`;
    }
    if (networkState === 'degraded') {
      return 'Weak connection — retrying automatically…';
    }
    return 'Offline — retrying connection…';
  })();

  return (
    <div
      role={networkState === 'offline' ? 'alert' : 'status'}
      aria-live="polite"
      className="pointer-events-none fixed left-1/2 top-[max(0.5rem,env(safe-area-inset-top))] z-[60] -translate-x-1/2"
    >
      <div className={`${PILL_BASE} ${PILL_TONES[networkState]}`}>
        {icon}
        <span>{message}</span>
        {offline && pendingCount > 0 && (
          <span className="opacity-80">
            · {pendingCount} pending action{pendingCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
